/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/usePinListData.ts
 🗓️ Created: 2025-01-27
 🧩 Description:
    Hook for fetching PIN List data.
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useI18n } from '../../../hooks/useI18n';
import { fetchPinListById } from '../api/pinListsApi';
import { FormState, PinMember, PinListResponse } from '../types';
import { getDefaultFormState, DEFAULT_MEMBER, createTempId } from '../utils/formDefaults';
import { mapApiMember } from '../utils/memberHelpers';

/**
 * Hook for fetching PIN List data
 */
export function usePinListData(id: string | undefined, duplicateId: string | null) {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<FormState>(getDefaultFormState());
  const [members, setMembers] = useState<PinMember[]>([]);
  const { showError } = useNotifications();
  const { t } = useI18n();
  const showErrorRef = useRef(showError);
  const lastLoadedIdRef = useRef<string | null>(null);
  const lastLocationKeyRef = useRef<string>('');

  useEffect(() => {
    showErrorRef.current = showError;
  }, [showError]);

  const loadPinList = useCallback(
    async (entryId: string, duplicate: boolean = false) => {
      setIsLoading(true);
      try {
        const entry = await fetchPinListById(entryId);
        const loadedMembers = Array.isArray(entry.members)
          ? entry.members.map((member, index) => mapApiMember(member, index))
          : [];

        const loaded: FormState = {
          name: entry.name || '',
          description: entry.description || '',
          enabled: entry.enabled ?? true
        };

        const copySuffix = t('common.copySuffix', 'Copy');
        const finalData = duplicate
          ? {
              ...loaded,
              name: loaded.name ? `${loaded.name} ${copySuffix}`.trim() : loaded.name
            }
          : loaded;

        setFormData(finalData);
        setMembers(loadedMembers);
      } catch (error) {
        showErrorRef.current(t('pinLists.messages.loadError'));
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [t]
  );

  useEffect(() => {
    const loadTargetId = duplicateId || id;
    const savedDataKey = 'pinLists:saveAndNewData';
    const locationKey = location.key || location.pathname + location.search;

    if (!loadTargetId || loadTargetId === 'new') {
      // Check if we need to reset:
      // 1. ID changed
      // 2. Location key changed (React Router navigation)
      // 3. sessionStorage timestamp indicates reset needed
      const resetTimestamp = sessionStorage.getItem(savedDataKey + ':reset');
      const lastResetTimestamp = lastLoadedIdRef.current === 'new' ? 
        sessionStorage.getItem(savedDataKey + ':lastReset') : null;
      
      const shouldReset = lastLoadedIdRef.current !== 'new' || 
                          lastLocationKeyRef.current !== locationKey ||
                          (lastLoadedIdRef.current === 'new' && loadTargetId === 'new' && 
                           resetTimestamp && resetTimestamp !== lastResetTimestamp);
      
      if (shouldReset) {
        lastLoadedIdRef.current = 'new';
        lastLocationKeyRef.current = locationKey;
        
        // Clear any saved data to ensure fresh form
        try {
          sessionStorage.removeItem(savedDataKey);
          if (resetTimestamp) {
            sessionStorage.setItem(savedDataKey + ':lastReset', resetTimestamp);
            sessionStorage.removeItem(savedDataKey + ':reset');
          }
        } catch (error) {
          // Ignore errors when clearing
        }
        
        // Only set default if not in duplicate mode
        if (!duplicateId) {
          setFormData(getDefaultFormState());
          setMembers([]);
        }
      }
      return;
    }

    if (lastLoadedIdRef.current === loadTargetId && lastLocationKeyRef.current === locationKey) {
      return;
    }

    lastLoadedIdRef.current = loadTargetId;
    lastLocationKeyRef.current = locationKey;
    loadPinList(loadTargetId, Boolean(duplicateId)).catch(() => {
      lastLoadedIdRef.current = null;
    });
  }, [duplicateId, id, loadPinList, location.key, location.pathname, location.search]);

  return {
    isLoading,
    formData,
    setFormData,
    members,
    setMembers
  };
}

